import {
  formDataToCustomizationData,
  stripCustomizationDefaults,
  validateForm,
} from "@components/CustomizeDonationExperience/helpers";

import {
  patchDonationFlowCustomizationRouteSpec,
  postDonationFlowCustomizationRouteSpec,
  resetDonationFlowCustomizationRouteSpec,
} from "@every.org/common/src/routes/donationFlowCustomization";

import { dispatchAdminPageAction } from "src/context/NonprofitAdminPageContext";
import { useDonationFormCustomization } from "src/context/NonprofitAdminPageContext/hooks";
import {
  NonprofitAdminPageContextActionType,
  SaveDonationFlowCustomizationArgs,
} from "src/context/NonprofitAdminPageContext/types";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

export const showAdminSidebar = () => {
  dispatchAdminPageAction({
    type: NonprofitAdminPageContextActionType.SHOW_SIDEBAR,
  });
};

export const hideAdminSidebar = () => {
  dispatchAdminPageAction({
    type: NonprofitAdminPageContextActionType.HIDE_SIDEBAR,
  });
};

export const toggleAdminSidebar = () => {
  dispatchAdminPageAction({
    type: NonprofitAdminPageContextActionType.TOGGLE_SIDEBAR,
  });
};

export const enableAdminSidebar = () => {
  dispatchAdminPageAction({
    type: NonprofitAdminPageContextActionType.ENABLE_SIDEBAR,
  });
};

export const disableAdminSidebar = () => {
  dispatchAdminPageAction({
    type: NonprofitAdminPageContextActionType.DISABLE_SIDEBAR,
  });
};

export const useSaveDonationFlowCustomization = () => {
  const donationFlowCustomization = useDonationFormCustomization();

  return async ({
    values,
    nonprofit,
    setError,
  }: SaveDonationFlowCustomizationArgs) => {
    if (!validateForm(values, nonprofit, setError)) {
      return false;
    }

    dispatchAdminPageAction({
      type: NonprofitAdminPageContextActionType.SAVE_DONATION_FLOW_CUSTOMIZATION_START,
    });

    try {
      const customizationData = formDataToCustomizationData(values);
      const existingCustomizationId = donationFlowCustomization.value?.id;
      const shouldCreate =
        donationFlowCustomization.hasNoExistingCustomization ||
        !existingCustomizationId;

      const customizationDataToSave = shouldCreate
        ? stripCustomizationDefaults(customizationData)
        : customizationData;

      let response;
      if (shouldCreate) {
        response = await queryApi(postDonationFlowCustomizationRouteSpec, {
          routeTokens: {
            nonprofitId: nonprofit.id,
          },
          queryParams: {},
          body: {
            donateButton: false,
            active: true,
            customization: customizationDataToSave,
          },
        });
      } else {
        response = await queryApi(patchDonationFlowCustomizationRouteSpec, {
          routeTokens: {
            id: existingCustomizationId,
          },
          queryParams: {},
          body: {
            customization: customizationDataToSave,
          },
        });
      }

      dispatchAdminPageAction({
        type: NonprofitAdminPageContextActionType.SAVE_DONATION_FLOW_CUSTOMIZATION_SUCCESS,
        customization: response,
      });

      return true;
    } catch (error) {
      logger.error({
        message: "Failed to save donation form customization",
        error,
      });

      dispatchAdminPageAction({
        type: NonprofitAdminPageContextActionType.SAVE_DONATION_FLOW_CUSTOMIZATION_ERROR,
        errorMessage:
          "Unable to save donation form customization. Please try again.",
      });

      return false;
    }
  };
};

export const useResetDonationFlowCustomization = () => {
  return async (nonprofitId: ContextNonprofit["id"]) => {
    dispatchAdminPageAction({
      type: NonprofitAdminPageContextActionType.SAVE_DONATION_FLOW_CUSTOMIZATION_START,
    });

    try {
      await queryApi(resetDonationFlowCustomizationRouteSpec, {
        routeTokens: {
          nonprofitId,
        },
        queryParams: {},
        body: {},
      });

      dispatchAdminPageAction({
        type: NonprofitAdminPageContextActionType.FETCH_DONATION_FLOW_CUSTOMIZATION_SUCCESS,
        customization: null,
      });

      return true;
    } catch (error) {
      logger.error({
        message: "Failed to reset donation form customization",
        error,
      });

      dispatchAdminPageAction({
        type: NonprofitAdminPageContextActionType.SAVE_DONATION_FLOW_CUSTOMIZATION_ERROR,
        errorMessage:
          "Unable to reset donation form customization. Please try again.",
      });

      return false;
    }
  };
};
